import React, { useState, useEffect } from 'react';
import {
  ChipSelect,
  SelectOption,
} from '@leasequery/lq.react-component-library';
import { ReportField, StandardReport, Company } from '../../../../models';
import { useAuth } from '../../../../context';
import { fetchReportFieldDropDownOptionsWithCaches } from '../../../../api';
import { useFeatureFlags } from '../../../../context/featureFlags';
import { FeatureFlagKeys } from '../../../../constants/featureFlags';

interface ReportFieldDropDownProps {
  field: ReportField;
  className?: string;
  value?: SelectOption<number>[];
  standardReport?: StandardReport;
  name?: string;
  onChange?: (options: SelectOption<number>[], name?: string) => void;
}

function ReportFieldDropDown({
  field,
  className,
  value,
  name,
  onChange,
  standardReport = {} as StandardReport,
}: ReportFieldDropDownProps): JSX.Element {
  const { client = {} as Company } = useAuth();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [options, setOptions] = useState<SelectOption<number>[]>([]);
  const [selectedOptions, setSelectedOptions] = useState(value || []);
  const { isFeatureEnabled } = useFeatureFlags();
  const isFieldSortedAndSearchable = isFeatureEnabled(FeatureFlagKeys.ReportingSearchableCustomReportFields);

  useEffect(() => {
    setIsDataLoading(true);
    const fetchData = async (): Promise<void> => {
      const dropdownOptions = await fetchReportFieldDropDownOptionsWithCaches(
        field.id,
        client.clientId,
        field.isClientCustom,
        field.isAllocation,
        field.isDimension,
        field.isDimensionItemId,
        standardReport.reportId,
        isFieldSortedAndSearchable,
      );
      const selectedOptionValues = new Set(
        (value || []).map((opt) => opt.value),
      );
      const nextSelectedOptions = dropdownOptions.filter((opt) =>
        selectedOptionValues.has(opt.value),
      );
      setOptions(dropdownOptions);
      setSelectedOptions(nextSelectedOptions);
      setIsDataLoading(false);
    };
    fetchData();
  }, [
    client.clientId,
    field.id,
    field.isClientCustom,
    field.isAllocation,
    standardReport.reportId,
    value,
    field.category,
    field.isDimension,
    field.isDimensionItemId,
    isFieldSortedAndSearchable,
  ]);
  return (
    <ChipSelect
      searchable={isFieldSortedAndSearchable}
      className={className}
      name={name}
      onChange={onChange}
      options={options}
      value={selectedOptions}
      disabled={isDataLoading}
      placeholder={isDataLoading ? 'Loading...' : ''}
    />
  );
}

export default ReportFieldDropDown;
